import {
  FeatureName,
  useCurrentAccount,
  useCurrentWorkspace,
  useIsFeatureEnabled,
  useIsFeatureEnabledFunc,
  WorkspaceType,
} from '@gripp/shared-logic';
import { useCallback } from 'react';
import {
  IMenu,
  MenuItems,
  OemHomeMenuItems,
  PulseHomeMenuItems,
} from '../components/menuItems';
import { MenuItems as MenuItemsOld } from '../components/menuItems-old';

export const useMenuPermission = () => {
  const { account } = useCurrentAccount();
  const workspace = useCurrentWorkspace(true);
  const isFeatureEnabled = useIsFeatureEnabledFunc();

  // TODO: Crop Circle Redesign | Feature Flag can be removed after the release
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(FeatureName.CropCircleRedesign);

  // OEM Home Dashboard: when the flag is on for an OEM workspace, consume the
  // explicit Home-first reordered list. Every other case (flag off, non-OEM,
  // legacy skin) uses today's MenuItems untouched so flag-off is pixel-identical
  // (incl. order — there is a regression test for this).
  const isOemHomeDashboardEnabled = useIsFeatureEnabled(
    FeatureName.OemHomeDashboard
  );
  const useOemHomeMenu =
    isCropCircleRedesignEnabled &&
    isOemHomeDashboardEnabled &&
    workspace?.type === WorkspaceType.Oem;

  // Pulse Home Dashboard: when the flag is on for a Producer workspace, consume
  // the Home-first Pulse list (mirrors useOemHomeMenu). Every other case (flag
  // off, non-Producer, legacy skin) keeps today's MenuItems untouched so
  // flag-off stays pixel-identical (incl. order — there is a regression test).
  const isPulseHomeDashboardEnabled = useIsFeatureEnabled(
    FeatureName.PulseHomeDashboard
  );
  const usePulseHomeMenu =
    isCropCircleRedesignEnabled &&
    isPulseHomeDashboardEnabled &&
    workspace?.type === WorkspaceType.Producer;

  const menuItems = useOemHomeMenu
    ? OemHomeMenuItems
    : usePulseHomeMenu
    ? PulseHomeMenuItems
    : isCropCircleRedesignEnabled
    ? MenuItems
    : MenuItemsOld;

  const menuList = useCallback(
    (): IMenu[][] =>
      menuItems.map((group) =>
        group.filter((link) => {
          if (link.requiredFeature && !isFeatureEnabled(link.requiredFeature)) {
            return false;
          }

          if (
            link.requiredWorkspaceType &&
            workspace?.type !== link.requiredWorkspaceType
          ) {
            return false;
          }

          const isWorkspaceRequired =
            typeof link.isWorkspaceRequired === 'function'
              ? link.isWorkspaceRequired(account)
              : link.isWorkspaceRequired;

          if (isWorkspaceRequired && !workspace) {
            return false;
          }

          return (
            link.requiredPermission &&
            account?.hasPermission(link.requiredPermission)
          );
        })
      ).filter((items) => items.length > 0),
    [isFeatureEnabled, account, workspace]
  );

  return menuList;
};
